import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './CameraThumbnailList.css';
import { RankedCamera } from '../../utils/CameraRankingUtils';
import { getFrameImagePath } from '../../services/colmapLoader';
import { useAppStore } from '../../store/appStore';
import { Camera } from '../../types/colmap';
import { distance } from '../../utils/vectorMath';
import { calculateScales, calculateCameraSpaceDepth, calculateFocalLength } from '../../utils/ThumbnailScaleCalculator';
import { FrameRenderer } from './FrameRenderer';
import { CameraSelector } from './CameraSelector';

interface ScaleData {
  circleScale: number
  distanceScale: number
  circleRadiusPx: number
  depth: number
  focalLength: number
}

interface CameraThumbnailListProps {
  cameras: RankedCamera[];
  projections?: Map<number, [number, number]>;
  onSelect: (cameraId: number, projectedPixel?: [number, number], radiusPxImageSpace?: number, scaleData?: ScaleData) => void;
  onClose: () => void;
  visible: boolean;
  showDebugOverlay?: boolean;
  point3D?: [number, number, number];
  allCameras?: Camera[];
  circleRadiusPx?: number;
  tappedFrameIndex?: number;
  playbackTrigger?: number;
  tapScreenY?: number;
  onUpdateRadius?: (radiusPx: number) => void;
}

export const CameraThumbnailList: React.FC<CameraThumbnailListProps> = ({
  cameras,
  projections,
  onSelect,
  onClose,
  visible,
  showDebugOverlay = false,
  point3D,
  allCameras,
  circleRadiusPx,
  tappedFrameIndex,
  playbackTrigger,
  tapScreenY,
  onUpdateRadius
}) => {
  const shopId = useAppStore(state => state.currentShopId)
  const currentSectionFramesPath = useAppStore(state => state.currentSectionFramesPath)
  const playingThumbnailIndex = useAppStore(state => state.playingThumbnailIndex)
  const setPlayingThumbnailIndex = useAppStore(state => state.setPlayingThumbnailIndex)
  
  const [isExpanded, setIsExpanded] = useState(true)
  const [isCompacted, setIsCompacted] = useState(false)
  const [isExpandedState, setIsExpandedState] = useState(false)
  const [isMinimizing, setIsMinimizing] = useState(false)
  const [isExpandingFromMinimize, setIsExpandingFromMinimize] = useState(false)
  const [isPlaybackActive, setIsPlaybackActive] = useState(false)
  const [isSelectorOpen, setIsSelectorOpen] = useState(false)
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight })
  
  // Refs
  const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const playingThumbnailRef = useRef<HTMLDivElement | null>(null)
  const playerOverlayRef = useRef<HTMLDivElement | null>(null)
  const lastTriggerRef = useRef<number>(0)

  // Track window resize and fullscreen changes
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight })
    }
    
    window.addEventListener('resize', handleResize)
    document.addEventListener('fullscreenchange', handleResize)
    
    return () => {
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('fullscreenchange', handleResize)
    }
  }, [])

  // Respond to playback trigger from badge click
  useEffect(() => {
    if (playbackTrigger !== undefined && playbackTrigger !== lastTriggerRef.current) {
      lastTriggerRef.current = playbackTrigger
      setIsExpanded(true)  // Un-minimize if minimized
      setIsExpandedState(true)
      setIsPlaybackActive(true)
    }
  }, [playbackTrigger])

  // Manage local thumbnail playback loop
  useEffect(() => {
    console.log(`[⏯️ PLAYBACK EFFECT] isPlaybackActive=${isPlaybackActive}, cameras=${cameras.length}`)
    
    if (!isPlaybackActive || cameras.length === 0) {
      console.log(`[⏸️ PLAYBACK STOPPING] Clearing interval`)
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
        playbackIntervalRef.current = null
      }
      return
    }

    console.log(`▶️ [Thumbnail Playback] Starting - cycling ${cameras.length} thumbnails`)

    // Initialize to first camera if not set (use getState to avoid subscription)
    const currentPlayingIndex = useAppStore.getState().playingThumbnailIndex
    if (currentPlayingIndex === null) {
      console.log(`▶️ [Thumbnail Playback] Initializing to first frame: ${cameras[0]?.frameIndex}`)
      setPlayingThumbnailIndex(cameras[0]?.frameIndex ?? 0)
    }

    // Create callback that has access to current cameras array
    const playNextFrame = () => {
      const currentState = useAppStore.getState()
      const current = currentState.playingThumbnailIndex
      
      if (current === null) {
        console.log(`▶️ [Thumbnail Playback] First frame: ${cameras[0]?.frameIndex}`)
        currentState.setPlayingThumbnailIndex(cameras[0]?.frameIndex ?? 0)
        return
      }
      
      // Find current camera in list
      const currentPos = cameras.findIndex(cam => cam.frameIndex === current)
      if (currentPos === -1) {
        console.log(`▶️ [Thumbnail Playback] Frame ${current} not found, resetting to first`)
        currentState.setPlayingThumbnailIndex(cameras[0]?.frameIndex ?? 0)
        return
      }
      
      // Go to next, wrap around
      const nextPos = (currentPos + 1) % cameras.length
      const nextFrame = cameras[nextPos].frameIndex
      console.log(`▶️ [Thumbnail Playback] Pos ${nextPos}/${cameras.length}: Frame ${nextFrame}`)
      currentState.setPlayingThumbnailIndex(nextFrame)
    }

    // Cycle through thumbnail indices at 300ms interval
    playbackIntervalRef.current = setInterval(playNextFrame, 300)

    return () => {
      console.log(`[🛑 PLAYBACK CLEANUP] Clearing interval`)
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
        playbackIntervalRef.current = null
      }
    }
  }, [isPlaybackActive])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
      }
    }
  }, [])

  // Scroll to playing thumbnail when playback starts
  useEffect(() => {
    if (isPlaybackActive && playingThumbnailIndex !== null && playingThumbnailRef.current) {
      playingThumbnailRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      })
    }
  }, [isPlaybackActive])
  
  // Dynamic thumbnail width measurement
  const [calculatedThumbnailWidth, setCalculatedThumbnailWidth] = useState(120)
  const thumbnailItemRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  
  // UNIFIED SCALING ARCHITECTURE:
  // - baseGeometryScalesRef: Per-camera geometry scale (circle formula OR distance)
  // - baseCalibrationRef: Initial geometric calibration (e.g., 0.280 = desiredScale/distance)
  // - knobScaleFactorRef: baseCalibration × userAdjustment (combined factor)
  // - Final scale = baseGeometryScale × knobScaleFactor
  const initialRadiusRef = useRef<number | null>(null) // Tracks initial knob radius for factor calculation
  const baseGeometryScalesRef = useRef<Map<number, number>>(new Map()) // Per-camera base scale
  const baseCalibrationRef = useRef<number>(1.0) // Initial geometric calibration (never changes after first tap)
  const knobScaleFactorRef = useRef<number>(1.0) // Global knob adjustment (baseCalibration × userAdjustment)
  const lastPoint3DRef = useRef<[number, number, number] | undefined>(undefined)
  const lastCameraCountRef = useRef<number>(0)
  const lastTappedFrameRef = useRef<number | undefined>(undefined)
  
  // Debug: Log circleRadiusPx prop value
  console.log(`🔍 [CameraThumbnailList RENDER] circleRadiusPx prop: ${circleRadiusPx?.toFixed(1) ?? 'undefined'}`)
  
  // CRITICAL: Reset scale factors DURING render when point3D OR camera count changes
  if (point3D !== lastPoint3DRef.current || cameras.length !== lastCameraCountRef.current) {
    const prevPoint = lastPoint3DRef.current
    const prevCount = lastCameraCountRef.current
    lastPoint3DRef.current = point3D
    lastCameraCountRef.current = cameras.length
    if (prevPoint !== undefined || prevCount !== 0) {
      // New tap or different camera set detected - reset geometry scales AND knob factor
      initialRadiusRef.current = null
      baseGeometryScalesRef.current.clear()
      baseCalibrationRef.current = 1.0 // Reset calibration to neutral
      knobScaleFactorRef.current = 1.0 // Reset knob to neutral
      console.log('🔄 [Unified Scaling] New tap detected - reset geometry scales + knob factor (was: ' + prevCount + ', now: ' + cameras.length + ')')
    }
  }
  
  // RECALIBRATE when tapped frame changes (thumbnail selection)
  if (tappedFrameIndex !== undefined && tappedFrameIndex !== lastTappedFrameRef.current) {
    lastTappedFrameRef.current = tappedFrameIndex
    
    // Only recalibrate if we have geometry already calculated (not initial tap)
    const selectedFrameGeometry = baseGeometryScalesRef.current.get(tappedFrameIndex)
    if (selectedFrameGeometry && circleRadiusPx !== undefined && allCameras && tappedFrameIndex < allCameras.length) {
      const selectedCamera = allCameras[tappedFrameIndex]
      const imageWidth = selectedCamera.intrinsics.width
      const desiredScale = imageWidth / (2 * circleRadiusPx)
      baseCalibrationRef.current = desiredScale / selectedFrameGeometry
      knobScaleFactorRef.current = baseCalibrationRef.current
      initialRadiusRef.current = circleRadiusPx
      
      console.log(`\n🔄 [RECALIBRATION - RENDER PHASE] Frame ${tappedFrameIndex} selected:`)
      console.log(`   🔵 Current circle radius: ${circleRadiusPx.toFixed(1)}px`)
      console.log(`   📐 Desired scale: ${desiredScale.toFixed(2)}x (fills thumbnail)`)
      console.log(`   🏗️  Base geometry: ${selectedFrameGeometry.toFixed(2)}x (cached)`)
      console.log(`   🎛️  New calibration: ${baseCalibrationRef.current.toFixed(3)} (= ${desiredScale.toFixed(2)} / ${selectedFrameGeometry.toFixed(2)})`)
      console.log(`   ✅ Recalibrated BEFORE render - thumbnail will match circle\n`)
    }
  }
  
  
  // Auto-collapse when new tap (point3D changes)
  useEffect(() => {
    if (isExpandedState) setIsExpandedState(false)
  }, [point3D])

  // Hide list if cameras found but no valid 3D point
  useEffect(() => {
    if (visible && cameras.length > 0 && !point3D) onClose()
  }, [visible, point3D, cameras.length, onClose])

  // Measure thumbnail width dynamically using ResizeObserver
  useEffect(() => {
    if (!scrollContainerRef.current) {
      console.log('[ResizeObserver] Scroll container ref not attached yet, skipping')
      return
    }
    
    console.log('[ResizeObserver] Setting up observer on scroll container')
    
    const observer = new ResizeObserver(() => {
      // Get the actual width of the first child thumbnail item
      const firstChild = scrollContainerRef.current?.querySelector('.camera-thumbnail-item') as HTMLElement
      if (firstChild) {
        const width = firstChild.offsetWidth
        console.log('[ResizeObserver] Thumbnail width detected: ' + width + 'px (isExpandedState: ' + isExpandedState + ')')
        setCalculatedThumbnailWidth(width)
      }
    })
    
    observer.observe(scrollContainerRef.current)
    
    return () => {
      console.log('[ResizeObserver] Cleaning up observer')
      observer.disconnect()
    }
  }, [isExpandedState])

  // Create stable key from camera list to detect actual content changes (not just reference changes)
  const cameraListKey = useMemo(() => {
    const key = cameras.map(c => c.frameIndex).join(',')
    console.log(`[🔑 CAMERA LIST KEY] Recalculated: "${key.substring(0, 50)}..." (${cameras.length} cameras)`)
    return key
  }, [cameras])

  // Memoize override camera lookup - only recalculate when:
  // 1. playingThumbnailIndex changes (different camera selected)
  // 2. camera list content actually changes (different frameIndices, not just new array reference)
  const overrideCamera = useMemo(() => {
    const found = playingThumbnailIndex !== null 
      ? cameras.find(c => c.frameIndex === playingThumbnailIndex)
      : undefined
    console.log(`[🎬 OVERRIDE CAMERA] Recalculated: playingIndex=${playingThumbnailIndex}, foundId=${found?.id}, foundFrame=${found?.frameIndex}`)
    return found
  }, [playingThumbnailIndex, cameraListKey])

  if (!visible || !shopId) {
    return null;
  }

  const handleCollapseClick = () => {
    if (!isCompacted) {
      // First tap: compact to item size
      setIsExpandedState(false)
            setIsCompacted(true)
    } else {
      // Second tap: minimize to sidebar
      setIsMinimizing(true)
      setTimeout(() => {
        setIsCompacted(false)
        setIsExpanded(false)
        setIsMinimizing(false)
      }, 1000)
    }
  }

  const handleExpand = () => {
    setIsExpandingFromMinimize(true)
    setIsExpanded(true)
    setIsExpandedState(false)
            setIsCompacted(true)
    setTimeout(() => {
      setIsExpandingFromMinimize(false)
    }, 1000)
  }

  const handleToggleExpandedState = () => {
    setIsExpandedState(!isExpandedState)
  }

  // Collapsed sidebar mode
  if (!isExpanded) {
    return (
      <div className="camera-thumbnail-collapsed">
        <button
          className="camera-thumbnail-collapsed-button"
          onClick={handleExpand}
          title={`${cameras.length} camera${cameras.length !== 1 ? 's' : ''} available`}
        >
          <svg
            className="camera-thumbnail-collapsed-icon"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          {cameras.length > 0 && (
            <span className="camera-thumbnail-collapsed-badge">{cameras.length}</span>
          )}
        </button>
      </div>
    )
  }

  // Dynamic positioning based on tap location
  const viewportHeight = window.innerHeight
  const isTopHalf = tapScreenY !== undefined && tapScreenY < viewportHeight / 2
  
  return (
    <div 
      className={`camera-thumbnail-list ${isMinimizing ? 'minimizing' : ''} ${isExpandingFromMinimize ? 'expanding-from-minimize' : ''}`}
      style={{
        bottom: (isExpandedState || isTopHalf) ? '20px' : 'auto',
        top: (!isExpandedState && !isTopHalf) ? '20px' : 'auto',
        left: isExpandedState ? '50%' : '0',
        transform: isExpandedState ? 'translate(-50%, 0)' : 'none',
        width: isExpandedState ? '80vw' : (isCompacted ? '120px' : '100vw')
      }}
    >
      {/* Scrollable thumbnail list - takes full height */}
      <div 
        ref={scrollContainerRef}
        className="camera-thumbnail-scroll" 
        style={{
          overflowX: isPlaybackActive ? 'hidden' : 'auto',
          height: '100%'
        }}>
        {cameras.map((camera, index) => {
          // During playback: hide all except the currently playing camera
          const isPlaying = isPlaybackActive && camera.frameIndex === playingThumbnailIndex
          const shouldHide = isPlaybackActive && !isPlaying
          
          return (
            <div
              key={camera.id}
              style={{
                display: shouldHide ? 'none' : 'block'
              }}
            >
              <FrameRenderer
                camera={camera}
                index={index}
                shopId={shopId}
                currentSectionFramesPath={currentSectionFramesPath}
                isExpandedState={isExpandedState}
                calculatedThumbnailWidth={calculatedThumbnailWidth}
                point3D={point3D}
                allCameras={allCameras}
                circleRadiusPx={circleRadiusPx}
                tappedFrameIndex={tappedFrameIndex}
                projections={projections}
                playingThumbnailIndex={playingThumbnailIndex}
                initialRadiusRef={initialRadiusRef}
                baseGeometryScalesRef={baseGeometryScalesRef}
                baseCalibrationRef={baseCalibrationRef}
                knobScaleFactorRef={knobScaleFactorRef}
                playingThumbnailRef={playingThumbnailRef}
                onSelect={onSelect}
                isPlaybackActive={isPlaybackActive}
                setIsPlaybackActive={setIsPlaybackActive}
                playbackIntervalRef={playbackIntervalRef}
                setPlayingThumbnailIndex={setPlayingThumbnailIndex}
                overrideCamera={overrideCamera}
              />
            </div>
          )
        })}
      </div>

      {/* Header overlays bottom 10% of thumbnails */}
      <div 
        className="camera-thumbnail-header"
        onClick={(e) => {
          e.stopPropagation()
        }}
        style={{
          position: 'absolute',
          bottom: '15px',
          left: 0,
          right: 0,
          height: '10%',
          backdropFilter: 'blur(3px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          opacity: 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: 'auto',
          zIndex: 10
        }}
      >
        <div className="camera-thumbnail-header-info">
          <span className="camera-thumbnail-header-count">{cameras.length}</span>
        </div>
        <div className="camera-thumbnail-header-controls" style={{ position: 'relative' }}>
          {(isExpandedState || !isCompacted) && (
            <button
              className="camera-thumbnail-header-button camera-thumbnail-selector-button"
              onClick={() => {
                // Stop playback when opening settings
                if (isPlaybackActive) {
                  console.log(`[⏸️ SETTINGS CLICKED] Stopping playback before opening settings`)
                  if (playbackIntervalRef.current) {
                    clearInterval(playbackIntervalRef.current)
                    playbackIntervalRef.current = null
                  }
                  setIsPlaybackActive(false)
                  setPlayingThumbnailIndex(null)
                }
                setIsSelectorOpen(!isSelectorOpen)
              }}
              title="Sort & Filter"
            >
              ⚙️
            </button>
          )}
          {(isExpandedState || !isCompacted) && (
            <button
              className="camera-thumbnail-header-button camera-thumbnail-play-button"
              onClick={() => {
              if (isPlaybackActive) {
                console.log(`[⏸️ PAUSE CLICKED] Stopping playback`)
                // STOP playback - MUST clear interval BEFORE setting flag
                if (playbackIntervalRef.current) {
                  console.log(`[⏸️ PAUSE] Clearing interval ${playbackIntervalRef.current}`)
                  clearInterval(playbackIntervalRef.current)
                  playbackIntervalRef.current = null
                }
                setIsPlaybackActive(false)
                
                // Select the current playing frame in the thumbnail list
                const currentPlayingFrame = playingThumbnailIndex
                console.log(`[⏸️ PAUSE] Current playing frame: ${currentPlayingFrame}`)
                if (currentPlayingFrame !== null) {
                  const currentCamera = cameras.find(c => c.frameIndex === currentPlayingFrame)
                  console.log(`[⏸️ PAUSE] Found camera:`, currentCamera?.frameIndex)
                  if (currentCamera) {
                    const projectedPixel = projections?.get(currentPlayingFrame)
                    const worldRadiusToPass = initialRadiusRef.current || circleRadiusPx
                    console.log(`[⏸️ PAUSE] Selecting frame ${currentPlayingFrame}, pixel:`, projectedPixel)
                    onSelect(currentPlayingFrame, projectedPixel, worldRadiusToPass)
                    
                    // Scroll to the selected thumbnail after a brief delay to let display:none update
                    setTimeout(() => {
                      if (playingThumbnailRef.current) {
                        console.log(`[⏸️ PAUSE] Scrolling to frame ${currentPlayingFrame}`)
                        playingThumbnailRef.current.scrollIntoView({
                          behavior: 'auto',
                          block: 'center',
                          inline: 'center'
                        })
                      }
                      // Clear playing state after scroll completes
                      setPlayingThumbnailIndex(null)
                    }, 50)
                  }
                }
                
                console.log(`[⏸️ PAUSE] State updated - isPlaybackActive should now be false`)
              } else {
                console.log(`[▶️ PLAY CLICKED] Starting playback`)
                // START playback
                setPlayingThumbnailIndex(cameras[0]?.frameIndex ?? null)
                setIsPlaybackActive(true)  // This triggers useEffect to create interval
                setIsCompacted(true)  // Auto-collapse to compact mode
                console.log(`[▶️ PLAY] State updated - isPlaybackActive should now be true`)
              }
            }}
            title={isPlaybackActive ? 'Stop playback' : 'Play frames'}
          >
            {isPlaybackActive ? '⏸' : '▶'}
          </button>
          )}
          {!isCompacted && onUpdateRadius && circleRadiusPx && (
            <>
              <button
                className="camera-thumbnail-header-button"
                onClick={() => onUpdateRadius(circleRadiusPx * 0.85)}
                title="Zoom in"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="7" strokeWidth="2"/>
                  <line x1="11" y1="8" x2="11" y2="14" strokeWidth="2"/>
                  <line x1="8" y1="11" x2="14" y2="11" strokeWidth="2"/>
                  <line x1="16" y1="16" x2="20" y2="20" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <button
                className="camera-thumbnail-header-button"
                onClick={() => onUpdateRadius(circleRadiusPx * 1.15)}
                title="Zoom out"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="7" strokeWidth="2"/>
                  <line x1="8" y1="11" x2="14" y2="11" strokeWidth="2"/>
                  <line x1="16" y1="16" x2="20" y2="20" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </>
          )}
          <button
            className="camera-thumbnail-header-button camera-thumbnail-next-frame-button"
            style={{ display: 'none' }}
            onClick={() => {
              // Stop playback if active
              if (isPlaybackActive) {
                if (playbackIntervalRef.current) {
                  clearInterval(playbackIntervalRef.current)
                  playbackIntervalRef.current = null
                }
                setIsPlaybackActive(false)
              }
              // Advance to next frame in sequence (not array position)
              if (playingThumbnailIndex !== null) {
                const nextFrame = playingThumbnailIndex + 1
                console.log(`[⏭️ NEXT FRAME] Frame ${playingThumbnailIndex} → ${nextFrame}`)
                setPlayingThumbnailIndex(nextFrame)
              } else {
                // If no frame is selected, start from first thumbnail's frame
                const firstFrame = cameras[0]?.frameIndex ?? null
                console.log(`[⏭️ NEXT FRAME] Starting from frame ${firstFrame}`)
                setPlayingThumbnailIndex(firstFrame)
              }
            }}
            title="Next frame"
          >
            ⏭️
          </button>
          {!isExpandedState && (
            <button
              className="camera-thumbnail-header-button camera-thumbnail-collapse-button"
              onClick={handleCollapseClick}
              title={isCompacted ? 'Minimize' : 'Compact'}
            >
              {isCompacted ? '↓' : '←'}
            </button>
          )}
          {!isCompacted && (
            <button
              className="camera-thumbnail-header-button camera-thumbnail-expand-state-button"
              onClick={handleToggleExpandedState}
              title={isExpandedState ? 'Collapse expanded' : 'Expand'}
            >
              {isExpandedState ? '↓' : '↑'}
            </button>
          )}
          <button
            className="camera-thumbnail-header-button camera-thumbnail-header-close"
            onClick={() => {
              // Stop playback when closing
              if (isPlaybackActive) {
                setIsPlaybackActive(false)
                if (playbackIntervalRef.current) {
                  clearInterval(playbackIntervalRef.current)
                }
                setPlayingThumbnailIndex(null)
              }
              onClose()
            }}
            title="Close"
          >
            ✕
          </button>
          {isCompacted && (
            <button
              className="camera-thumbnail-header-button camera-thumbnail-expand-button"
              onClick={() => setIsCompacted(false)}
              title="Expand"
            >
              →
            </button>
          )}
          <CameraSelector isOpen={isSelectorOpen} onClose={() => setIsSelectorOpen(false)} />
        </div>
      </div>
    </div>
  );
};